// Localization settings (spec/dashboard.md section 9.4): the default language, Include key in display name, the language
// checklist over the static catalog with ↑ ↓ on the enabled ones, and the two presets. The whole form is one staged
// `localization-settings` draft (id `settings`) that Save All sends as one PATCH with If-Match of its version.
import { useParams } from 'react-router';
import { ApiError } from '../../api/client';
import { ops } from '../../api/operations';
import type { Json, LocalizationSettings } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { LANGUAGE_CATALOG, LANGUAGE_PRESETS, languageHeader, languageInfo } from '../../content/localization/languages';
import { useGame } from '../../shell/GameContext';
import { EmptyState } from '../../ui/Banner';
import { Button, IconButton } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { Field } from '../../ui/Field';
import { AlertCircle, ArrowDown, ArrowUp } from '../../ui/icons';
import { Select, Switch } from '../../ui/inputs';
import { useQuery } from '../../workspace/cache';
import { useOverlay, useWorkspace } from '../../workspace/react';
import { entityKey } from '../../workspace/Workspace';

type Language = LocalizationSettings['languages'][number];

/** Enabled languages in their order (renumbered 1…n), then the disabled ones; the full list the PATCH replaces. */
export function normalizeLanguages(list: Language[]): Language[] {
  const enabled = list.filter((l) => l.enabled).sort((a, b) => Number(a.order) - Number(b.order));
  const disabled = list.filter((l) => !l.enabled);
  return [...enabled.map((l, i) => ({ ...l, order: i + 1 })), ...disabled.map((l, i) => ({ ...l, order: enabled.length + i + 1 }))];
}

export function SettingsTab() {
  const { org, game, can } = useGame();
  const { branch = '' } = useParams();
  const { client } = useAuth();
  const workspace = useWorkspace();
  const canWrite = can('content_write') && !workspace.isProtected;
  // the same cache entry the ContentProvider reads (one request)
  const settingsQ = useQuery(`branch:${game.id}:${branch}:localization-settings`, () => client.data(ops.getLocalizationSettings, { path: { org, game: game.slug, branch } }), { head: workspace.head });
  const server = settingsQ.data as LocalizationSettings | undefined;
  const key = entityKey('localization-settings', 'settings');
  const current = useOverlay(key, server as unknown as Json) as unknown as LocalizationSettings | undefined;
  const draft = workspace.getDraft(key);

  if (!current || !server) {
    return (
      <div className="pane" data-testid="localization-settings-form">
        {settingsQ.error ? (
          <div className="pane-body">
            <EmptyState icon={<AlertCircle />} title="The settings could not be loaded" body={settingsQ.error instanceof ApiError ? settingsQ.error.message : String(settingsQ.error)} />
          </div>
        ) : (
          <div className="skeleton-rows" role="status" aria-label="Loading">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="skeleton-row">
                <span className="skeleton" />
                <span className="skeleton" />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const edit = (path: 'defaultLanguage' | 'includeKeyInDisplay' | 'languages', value: unknown) =>
    workspace.edit('localization-settings', 'settings', path, value, { base: { version: Number(server.version), value: server as unknown as Json }, label: 'Localization settings', template: 'Localization settings' });

  const list = current.languages ?? [];
  const enabled = list.filter((l) => l.enabled).sort((a, b) => Number(a.order) - Number(b.order));
  const byCode = new Map(list.map((l) => [l.code, l]));
  const def = current.defaultLanguage;
  const wasEnabled = (code: string) => !!server.languages?.find((l) => l.code === code && l.enabled);

  const setEnabled = (codes: string[], value: boolean) => {
    let next = [...list];
    let order = enabled.length;
    for (const code of codes) {
      if (!value && code === def) continue; // the default language cannot be disabled
      const existing = next.find((l) => l.code === code);
      if (existing) {
        if (existing.enabled === value) continue;
        next = next.map((l) => (l.code === code ? { ...l, enabled: value, order: value ? ++order : l.order } : l));
      } else if (value) {
        const info = languageInfo(code);
        next.push({ code, name: info.name, localizedName: info.localizedName, enabled: true, order: ++order });
      }
    }
    edit('languages', normalizeLanguages(next));
  };
  const move = (code: string, delta: -1 | 1) => {
    const order = enabled.map((l) => l.code);
    const i = order.indexOf(code);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= order.length) return;
    [order[i], order[j]] = [order[j]!, order[i]!];
    const rank = new Map(order.map((c, n) => [c, n + 1]));
    edit('languages', normalizeLanguages(list.map((l) => (rank.has(l.code) ? { ...l, order: rank.get(l.code)! } : l))));
  };

  // the checklist: the enabled ones in their order, then the rest of the catalog; `vn` only while it is (or was) enabled,
  // and any code the branch has that the catalog lacks
  const shown = new Set(enabled.map((l) => l.code));
  const rest = [
    ...LANGUAGE_CATALOG.filter((c) => !shown.has(c.code) && (!c.deprecated || wasEnabled(c.code))).map((c) => c.code),
    ...list.filter((l) => !shown.has(l.code) && !LANGUAGE_CATALOG.some((c) => c.code === l.code)).map((l) => l.code),
  ];
  const rows = [...enabled.map((l) => l.code), ...rest];

  return (
    <form className="pane loc-settings" data-testid="localization-settings-form" onSubmit={(e) => e.preventDefault()}>
      <div className="pane-head">
        <h2>Settings</h2>
        {draft && <Badge tone="warning">Staged</Badge>}
        <span className="spacer" />
        <span className="muted small">Saved with Save All: every change below stages the one localization-settings change.</span>
      </div>
      <div className="pane-body">
        {(draft?.error || draft?.conflict) && (
          <div className="loc-alert" role="alert">
            <AlertCircle className="loc-alert-icon" aria-hidden="true" />
            <div>
              {draft?.error && <p>{draft.error.message}</p>}
              {draft?.conflict && <p>{draft.conflict.message}</p>}
            </div>
          </div>
        )}
        <div className="form-grid">
          <Field label="Default language" help="Its column is required; it cannot be disabled.">
            <Select value={def} disabled={!canWrite} onChange={(e) => edit('defaultLanguage', e.target.value)} aria-label="Default language">
              {enabled.map((l) => (
                <option key={l.code} value={l.code}>
                  {languageHeader(l.code, l.name)}
                </option>
              ))}
            </Select>
          </Field>
          <div className="field loc-switch-field">
            <Switch label="Include key in display name" checked={!!current.includeKeyInDisplay} disabled={!canWrite} onChange={(v) => edit('includeKeyInDisplay', v)} />
          </div>
        </div>
        <section className="loc-settings-section">
          <div className="section-head">
            <h3>Languages</h3>
            <span className="chip">{enabled.length} enabled</span>
            <span className="spacer" />
            <span className="loc-presets" role="group" aria-label="Presets">
              <span className="muted small">Presets</span>
              {LANGUAGE_PRESETS.map((p) => (
                <Button key={p.key} size="s" disabled={!canWrite} onClick={() => setEnabled(p.codes, true)} title={`Enables ${p.codes.join(', ')} (keeps the others)`}>
                  {p.label}
                </Button>
              ))}
            </span>
          </div>
          <fieldset className="loc-languages">
            <legend className="visually-hidden">Languages</legend>
            <ul className="loc-language-list">
              {rows.map((code) => {
                const entry = byCode.get(code);
                const info = languageInfo(code);
                const on = !!entry?.enabled;
                const isDefault = code === def;
                const index = enabled.findIndex((l) => l.code === code);
                return (
                  <li key={code} className={`loc-language ${on ? 'on' : ''} ${isDefault ? 'default' : ''}`}>
                    <label className="check">
                      <input type="checkbox" checked={on} disabled={!canWrite || isDefault} onChange={(e) => setEnabled([code], e.target.checked)} aria-label={languageHeader(code, entry?.name ?? info.name)} />
                      <span className="loc-language-name">
                        {entry?.name ?? info.name} <code>{code}</code>
                        {(entry?.localizedName ?? info.localizedName) && <span className="muted"> · {entry?.localizedName ?? info.localizedName}</span>}
                      </span>
                    </label>
                    {isDefault && <Badge tone="accent">default</Badge>}
                    {info.deprecated && (
                      <Badge tone="warning" title="The legacy Vietnamese code; vi is the BCP-47 one. Kept for branches that have it, never offered anew.">
                        deprecated
                      </Badge>
                    )}
                    {on && (
                      <span className="loc-move">
                        <span className="loc-order">{index + 1}</span>
                        <IconButton label={`Move ${code} up`} disabled={!canWrite || index <= 0} onClick={() => move(code, -1)}>
                          <ArrowUp aria-hidden="true" />
                        </IconButton>
                        <IconButton label={`Move ${code} down`} disabled={!canWrite || index < 0 || index >= enabled.length - 1} onClick={() => move(code, 1)}>
                          <ArrowDown aria-hidden="true" />
                        </IconButton>
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </fieldset>
        </section>
      </div>
      <div className="pane-foot">
        <span className="muted small">The order is the grid's column order and the SDK's. The spreadsheet id for Google Sheets lives in the import / export dialog, remembered in this browser.</span>
      </div>
    </form>
  );
}
